import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import useCompanyParams from '../hooks/useCompanyParams';
import { LogOut, Circle, User } from 'lucide-react';
import FullscreenLoader from '../pages/FullscreenLoader';

const KioskLayout = () => {
  const { user, company, logout } = useAuth();
  const { companyInfo, loading: loadingCompany } = useCompanyParams();
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showLogoutLoader, setShowLogoutLoader] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);


  //FIX — Rechargement automatique à minuit (reset daily PWA)
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    const lastLoadDate = localStorage.getItem('hp_kiosk_load_date');

    if (lastLoadDate && lastLoadDate !== today) {
      console.log('🌅 [Kiosk] Nouveau jour détecté au démarrage, rechargement...');
      localStorage.setItem('hp_kiosk_load_date', today);
      window.location.reload(true);
      return;
    }

    localStorage.setItem('hp_kiosk_load_date', today);

    const dayCheckInterval = setInterval(() => {
      const nowDate = new Date().toISOString().split('T')[0];
      const stored = localStorage.getItem('hp_kiosk_load_date');
      if (stored !== nowDate) {
        console.log('🌅 [Kiosk] Minuit détecté, rechargement...');
        localStorage.setItem('hp_kiosk_load_date', nowDate);
        window.location.reload(true);
      }
    }, 60000);

    return () => clearInterval(dayCheckInterval);
  }, []);


  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleLogout = () => setShowLogoutLoader(true);

  const formatTime = (date) => date.toLocaleTimeString('fr-FR', {
    hour: '2-digit', minute: '2-digit', second: '2-digit'
  });

  const formatDate = (date) => date.toLocaleDateString('fr-FR', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
  });

  const formatDateShort = (date) => date.toLocaleDateString('fr-FR', {
    day: 'numeric', month: 'short', year: 'numeric'
  });

  const getCompanyName = () => {
    if (loadingCompany) return '...';
    return companyInfo?.nom || company?.name || 'HoraPilot';
  };

  const getUserName = () => {
    return user?.name || user?.full_name || user?.username || 'Utilisateur';
  };

  return (
    <div className="h-screen bg-[#fdfcf5] overflow-hidden">
      <div className="h-screen p-2 sm:p-4 xl:p-8 flex flex-col gap-2 sm:gap-4 xl:gap-6 overflow-hidden">

        {/* ═══ BARRE DU HAUT ═══ */}
        <div className="grid grid-cols-3 gap-2 sm:gap-4 xl:gap-6 flex-shrink-0">

          {/* ── Carte 1 : Logo + Nom Entreprise ── */}
          <div className="bg-white rounded-xl sm:rounded-2xl xl:rounded-3xl shadow-md border-l-4 border-[#448696] p-2 sm:p-4 xl:p-6 flex items-center gap-2 xl:gap-4 overflow-hidden">
            <div
              className="flex-shrink-0 w-8 h-8 sm:w-11 sm:h-11 xl:w-14 xl:h-14 rounded-lg sm:rounded-xl xl:rounded-2xl flex items-center justify-center shadow-md"
              style={{ backgroundColor: companyInfo?.couleur_primaire || '#448696' }}
            >
              {loadingCompany ? (
                <div className="animate-pulse w-6 h-6 bg-white/20 rounded" />
              ) : companyInfo?.logo_url ? (
                <img src={companyInfo.logo_url} alt="Logo" className="max-w-full max-h-full object-contain rounded-lg" />
              ) : (
                <span className="text-sm sm:text-base xl:text-2xl font-black text-white">
                  {companyInfo?.logo_initiales || company?.name?.substring(0, 2).toUpperCase() || 'HP'}
                </span>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-xs sm:text-base xl:text-2xl font-black text-gray-900 truncate leading-tight">
                {getCompanyName()}
              </h1>
              <p className="hidden sm:block text-xs xl:text-sm text-gray-500 font-medium truncate">
                Terminal de Pointage
              </p>
            </div>
          </div>

          {/* ── Carte 2 : Horloge ── */}
          <div className="bg-gradient-to-br from-[#448696] to-[#6da3af] rounded-xl sm:rounded-2xl xl:rounded-3xl shadow-xl p-2 sm:p-4 xl:p-6 flex flex-col items-center justify-center text-center overflow-hidden">
            <div className="text-xl sm:text-3xl xl:text-5xl font-black text-white tracking-tight leading-none">
              {formatTime(currentTime)}
            </div>
            <div className="hidden xl:block text-sm text-white/80 font-medium capitalize mt-1">
              {formatDate(currentTime)}
            </div>
            <div className="hidden sm:block xl:hidden text-xs text-white/80 font-medium capitalize mt-0.5">
              {formatDateShort(currentTime)}
            </div>
          </div>

          {/* ── Carte 3 : Utilisateur + Déconnexion ── */}
          <div className="bg-[#fed774] rounded-xl sm:rounded-2xl xl:rounded-3xl shadow-md p-2 sm:p-4 xl:p-6 flex items-center justify-between overflow-hidden gap-2">

            {/* PETIT ÉCRAN (< xl) : texte directement sur le jaune */}
            <button
              onClick={handleLogout}
              className="xl:hidden w-full h-full flex items-center justify-center gap-2 transition-all group"
            >
              <LogOut className="w-4 h-4 sm:w-5 sm:h-5 text-gray-800 group-hover:text-gray-900 flex-shrink-0" strokeWidth={2.5} />
              <span className="text-sm sm:text-base font-black text-gray-800 group-hover:text-gray-900 whitespace-nowrap">
                Déconnexion
              </span>
            </button>

            {/* GRAND ÉCRAN (≥ xl) : icône user + nom + statut + bouton */}
            <div className="hidden xl:flex items-center justify-between w-full gap-3">
              {/* Info utilisateur */}
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="flex-shrink-0 w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm">
                  <User className="w-6 h-6 text-gray-700" strokeWidth={2} />
                </div>
                <div className="min-w-0">
                  <p className="font-bold text-gray-900 text-sm truncate leading-tight">
                    {getUserName()}
                  </p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <Circle className={`flex-shrink-0 w-2 h-2 ${isOnline ? 'fill-green-600 text-green-600' : 'fill-red-600 text-red-600'}`} />
                    <span className="text-xs text-gray-700 font-medium whitespace-nowrap">
                      {isOnline ? 'En ligne' : 'Hors ligne'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Bouton déconnexion */}
              <button
                onClick={handleLogout}
                className="flex-shrink-0 flex items-center gap-2 px-4 py-2.5 bg-white/60 hover:bg-white rounded-xl transition-all group"
              >
                <LogOut className="w-5 h-5 text-gray-700 group-hover:text-gray-900" strokeWidth={2} />
                <span className="text-sm font-bold text-gray-700 group-hover:text-gray-900 whitespace-nowrap">
                  Déconnexion
                </span>
              </button>
            </div>

          </div>

        </div>

        {/* ═══ ZONE DE CONTENU ═══ */}
        <div className="flex-1 bg-white rounded-xl sm:rounded-2xl xl:rounded-3xl shadow-lg p-2 sm:p-4 xl:p-8 overflow-hidden min-h-0">
          <Outlet />
        </div>

      </div>

      {/* FullscreenLoader déconnexion */}
      <FullscreenLoader
        visible={showLogoutLoader}
        duration={1600}
        message="Au revoir · À bientôt !"
        onComplete={async () => {
          try {
            await logout();
            navigate('/login');
          } catch (err) {
            console.error('Erreur déconnexion:', err);
          }
        }}
      />
    </div>
  );
};

export default KioskLayout;